import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';

import {Loglevel} from '../model/logging/log-level';
import {LoggingEntry} from '../model/logging/logging-entry';
import {LoggingDetailsService} from './logging-details.service';
import {LoggingApiService} from './apiservices/logging-api.service';
import {Router} from "@angular/router";

const MAX_STACK_LINES = 5;

@Injectable({
    providedIn: 'root' // ✅ Ensures the service is available globally
})
export class LoggingService {
    private readonly validChars = new Set(
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789äöüÄÜÖ\'"[]{}@/.-_()+*?;,: \\'
    );

    private readonly knownApiStatuses = new Set([404]);

    private loggingDetailsService = inject(LoggingDetailsService);
    private loggingApiService = inject(LoggingApiService);
    private router= inject(Router);

    error(error: any, extraParams?: Map<string, any>): void {
        if (this.isKnownApiError(error)) {
            // known API errors are logged with level INFO
            this.info(error, extraParams);
        } else {
            this.log(error, extraParams, Loglevel.ERROR);
        }
    }

    info(error: any, extraParams?: Map<string, any>): void {
        this.log(error, extraParams, Loglevel.INFO);
    }

    warn(error: any, extraParams?: Map<string, any>): void {
        this.log(error, extraParams, Loglevel.WARNING);
    }

    private log(error: any, extraParams: Map<string, any> | undefined, loglevel: Loglevel): void {
        if (environment.doLog !== false) {
            try {
                const loggingDetails = this.loggingDetailsService.getUserDetails?.();
                if (!loggingDetails) {
                    console.warn('User details not available for logging.');
                    return;
                }

                error = this.format(error);
                const additionalInfo = JSON.stringify(error, this.getCircularReplacer());
                const extraParamsJson = extraParams ? JSON.stringify([...extraParams]) : null;

                const {
                    clientVersion,
                    device: {browser, deviceType, os, userAgentString}
                } = loggingDetails;

                const loggingEntry: LoggingEntry = {
                    level: loglevel,
                    message: this.toStringAndSanitize(error.message || error.response),
                    metaInfos: this.toStringAndSanitize({error: additionalInfo, extraParams: extraParamsJson, currentUrl: this.toStringAndSanitize(this.router.url)}),
                    stack: this.toStringAndSanitize(error.stack),
                    uiVersion: this.toStringAndSanitize(clientVersion),
                    browser: this.toStringAndSanitize(browser),
                    deviceType: this.toStringAndSanitize(deviceType),
                    operatingSystem: this.toStringAndSanitize(os),
                    userAgent: this.toStringAndSanitize(userAgentString)
                };

                if (environment.local) {
                    console.error('local log:', loggingEntry);
                } else {
                    this.loggingApiService.sendLogEvent(loggingEntry);
                }
            } catch (loggingError: any) {
                // prevent endless logging loop
                console.warn('LoggingService failed to log an error:', loggingError);
            }
        }
    }

    private isKnownApiError(error: any): boolean {
        return this.knownApiStatuses.has(error?.status);
    }

    private format(error: any): any {
        if (typeof error === 'string') {
            error = {message: error};
        }
        if (error.stack) {
            error.stack = error.stack.split('\n', MAX_STACK_LINES).join();
        }
        return error;
    }

    private toStringAndSanitize(obj: any): string {
        if (!obj) {
            return '';
        }
        const objAsString = typeof obj === 'string' ? obj : JSON.stringify(obj);
        return this.sanitizeWithReplacement(objAsString, this.validChars);
    }

    private sanitizeWithReplacement(input: string, validChars: Set<string>, replacer = '*INVALID*'): string {
        return [...input].map(char => validChars.has(char) ? char : replacer).join('');
    }


    private getCircularReplacer = () => {
        const seen = new WeakSet();
        return (_: any, value: any) => {
            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return null;
                }
                seen.add(value);
            }
            return value;
        };
    };
}



